import { computed, watch } from '@vue/composition-api';
import orderBy from 'lodash/orderBy';
import { useStorymapStore } from '../useModule';
import { filteredSubtasks, isFiltered, subtasksWithoutArchives } from './useFilterCommon';
import { hasFocusedRelease, activitiesInFocusedRelease, tasksInFocusedRelease } from './useFocusRelease';
import { CardColor } from '@/api/workspaceSettings/queries';
import { Activity, StorymapState, Subtask, Task, ViewType } from '../types';

// Base computed for hide mode active check (includes mutual exclusivity AND view type guard)
export const isHideUnfilteredActive = computed(() => {
    const state = useStorymapStore().state;

    // Only active in Discovery, Planning, Backlog views (AC23)
    const allowedViewTypes = [ViewType.Discovery, ViewType.Planning, ViewType.Backlog];
    if (!allowedViewTypes.includes(state.currentViewType)) {
        return false;
    }

    return state.filterState.hideUnfiltered && isFiltered.value && !hasFocusedRelease.value;
});

// Subtasks that should be considered for visibility (includes annotations)
export const hideUnfilteredMatchingSubtasks = computed(() => {
    const baseMatches = filteredSubtasks.value;

    // Always include transparent annotations for visibility purposes (AC19)
    const annotations = subtasksWithoutArchives.value.filter((s) => s.color === CardColor.TransparentAnnotation);

    // De-duplicate by id
    const byId = new Map<string, Subtask>();
    for (const s of [...baseMatches, ...annotations]) {
        byId.set(s.id, s);
    }

    return Array.from(byId.values());
});

// Centralized Set for newly created card IDs (convert array to Set for O(1) lookups)
export const newlyCreatedIdSet = computed(() => new Set(useStorymapStore().state.filterState.newlyCreatedCardIds));

// Precomputed id sets for performance
export const matchingSubtaskIdSet = computed(() => {
    const state = useStorymapStore().state;
    const filtered = new Set(hideUnfilteredMatchingSubtasks.value.map((s) => s.id));

    // Add newly created subtasks
    newlyCreatedIdSet.value.forEach(id => {
        if (state.subtasks.some(s => s.id === id)) {
            filtered.add(id);
        }
    });

    return filtered;
});

export const visibleTaskIdSet = computed(() => {
    const state = useStorymapStore().state;
    if (!isHideUnfilteredActive.value) {
        return new Set(state.tasks.map((t) => t.id));
    }

    const visible = new Set<string>();

    for (const task of state.tasks) {
        const hasMatchingSubtask = hideUnfilteredMatchingSubtasks.value.some(s => s.taskId === task.id);
        const isNewlyCreated = newlyCreatedIdSet.value.has(task.id);
        const hasNewSubtask = state.subtasks.some(s =>
            s.taskId === task.id && newlyCreatedIdSet.value.has(s.id)
        );

        if (hasMatchingSubtask || isNewlyCreated || hasNewSubtask) {
            visible.add(task.id);
        }
    }

    return visible;
});

export const visibleActivityIdSet = computed(() => {
    const state = useStorymapStore().state;
    if (!isHideUnfilteredActive.value) {
        return new Set(state.activities.map((a) => a.id));
    }

    const visible = new Set<string>();

    for (const activity of state.activities) {
        const hasVisibleTask = state.tasks.some(t =>
            t.activityId === activity.id && visibleTaskIdSet.value.has(t.id)
        );
        const isNewlyCreated = newlyCreatedIdSet.value.has(activity.id);
        const hasNewTask = state.tasks.some(t =>
            t.activityId === activity.id && newlyCreatedIdSet.value.has(t.id)
        );

        if (hasVisibleTask || isNewlyCreated || hasNewTask) {
            visible.add(activity.id);
        }
    }

    return visible;
});

// Tasks with at least one matching subtask OR newly created (global across all releases)
export const visibleTasks = computed(() => {
    if (!isHideUnfilteredActive.value) {
        return [];
    }

    const state = useStorymapStore().state;
    return state.tasks.filter((t) => visibleTaskIdSet.value.has(t.id));
});

// Activities with at least one visible task OR newly created
export const visibleActivities = computed(() => {
    if (!isHideUnfilteredActive.value) {
        return [];
    }

    const state = useStorymapStore().state;
    return state.activities.filter((a) => visibleActivityIdSet.value.has(a.id));
});

// Helper functions for keyboard navigation (AC14)
export const getVisibleActivitiesForNav = (state: StorymapState): Activity[] => {
    if (hasFocusedRelease.value) {
        return orderBy(activitiesInFocusedRelease.value, 'order');
    }
    if (isHideUnfilteredActive.value) {
        return orderBy(visibleActivities.value, 'order');
    }
    return orderBy(state.activities, 'order');
};

export const getVisibleTasksForNav = (state: StorymapState): Task[] => {
    let tasks: Task[];

    if (hasFocusedRelease.value) {
        tasks = tasksInFocusedRelease.value;
    } else if (isHideUnfilteredActive.value) {
        tasks = visibleTasks.value;
    } else {
        tasks = state.tasks;
    }

    // Respect collapsed activities
    const collapsedActivityIds = state.activities
        .filter((a) => a.isCollapsed)
        .map((a) => a.id);

    return tasks.filter((t) => !collapsedActivityIds.includes(t.activityId));
};

// Watch to reset hideUnfiltered when user manually clears all filters (AC3)
watch(isFiltered, (nowFiltered) => {
    const store = useStorymapStore();
    if (!nowFiltered && store.state.filterState.hideUnfiltered) {
        store.state.filterState.hideUnfiltered = false;
        // Clear newly created card tracking when filters are cleared
        store.state.filterState.newlyCreatedCardIds = [];
    }
});
